import React, { useState } from 'react';
import { X, Zap, RefreshCw } from 'lucide-react';
import { User } from '../../../types/user';

interface TokenManagementModalProps {
  isOpen: boolean;
  onClose: () => void;
  user: User;
  onUpdateLimit: (userId: number, newLimit: number | null) => Promise<void>;
  onResetTokens: (userId: number) => Promise<void>;
}

export const TokenManagementModal: React.FC<TokenManagementModalProps> = ({
  isOpen,
  onClose,
  user,
  onUpdateLimit,
  onResetTokens,
}) => {
  const [newLimit, setNewLimit] = useState(user.token_limit);
  const [inputValue, setInputValue] = useState(
    user.token_limit === null ? '∞' : user.token_limit.toString()
  );
  const [isUpdating, setIsUpdating] = useState(false);
  const [isResetting, setIsResetting] = useState(false);

  const UNLIMITED_VALUE = null;

  const usagePercentage = user.token_limit !== null && user.token_limit > 0
    ? Math.min(100, (user.token_used / user.token_limit) * 100)
    : 0;

  const handleUpdateLimit = async () => {
    if (newLimit !== null && newLimit < 0) {
      alert('Valor de limite inválido');
      return;
    }

    setIsUpdating(true);
    try {
      await onUpdateLimit(user.id, newLimit);
      onClose();
    } catch (error) {
      console.error('Erro ao atualizar limite:', error);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleResetTokens = async () => {
    const confirmed = window.confirm(
      `Resetar o contador de tokens de ${user.name}?\n\n` +
      `Isso irá zerar o uso atual (${user.token_used.toLocaleString()} tokens).`
    );

    if (!confirmed) return;

    setIsResetting(true);
    try {
      await onResetTokens(user.id);
      onClose();
    } catch (error) {
      console.error('Erro ao resetar tokens:', error);
    } finally {
      setIsResetting(false);
    }
  };

  const formatNumber = (num: number): string => {
    if (num >= 1000000) {
      return `${(num / 1000000).toFixed(1)}M`;
    }
    if (num >= 1000) {
      return `${(num / 1000).toFixed(0)}k`;
    }
    return num.toString();
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 w-screen h-screen bg-black/60 z-40 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-[#2a2a2a] rounded-xl p-6 z-50 w-full max-w-md shadow-2xl border border-[#444444]">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <div className="p-2 bg-[#F4CC04]/20 rounded-lg">
              <Zap className="w-5 h-5 text-[#F4CC04]" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white">Gerenciar Tokens</h3>
              <p className="text-sm text-[#888888]">{user.name}</p>
            </div>
          </div>
          <button
            className="text-[#888888] hover:text-white transition-colors"
            onClick={onClose}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Current Usage */}
        <div className="mb-6 p-4 bg-[#1a1a1a] rounded-lg border border-[#333333]">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-[#cccccc]">Uso Atual</span>
            {user.token_limit !== UNLIMITED_VALUE && (
              <span className="text-sm text-[#888888]">
                {Math.round(usagePercentage)}%
              </span>
            )}
          </div>

          <div className="text-3xl font-bold text-white mb-3">
            {formatNumber(user.token_used)}
            <span className="text-lg text-[#888888] font-normal ml-1">
              / {user.token_limit === UNLIMITED_VALUE ? '∞' : formatNumber(user.token_limit)}
            </span>
          </div>

          {user.token_limit !== UNLIMITED_VALUE && (
            <div className="w-full h-2 bg-[#333333] rounded-full overflow-hidden">
              <div
                className="h-full bg-[#F4CC04] transition-all duration-300"
                style={{ width: `${usagePercentage}%` }}
              />
            </div>
          )}
        </div>

        {/* New Limit Input */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-[#cccccc] mb-2">
            Novo Limite Mensal
          </label>
          <input
            type="text"
            className="
              w-full px-4 py-3
              bg-[#1a1a1a] border border-[#333333]
              text-white text-center
              rounded-lg
              focus:outline-none focus:border-[#F4CC04]
              transition-colors
            "
            value={inputValue}
            onChange={(e) => {
              const value = e.target.value;

              // Se for símbolo de infinito, define como null
              if (value === '∞') {
                setInputValue('∞');
                setNewLimit(UNLIMITED_VALUE);
                return;
              }

              // Se vazio, mantém vazio (será convertido para infinito no onBlur)
              if (value === '') {
                setInputValue('');
                setNewLimit(UNLIMITED_VALUE);
                return;
              }

              // Remove todos caracteres não-numéricos
              const numericValue = value.replace(/\D/g, '');

              if (numericValue === '') {
                setInputValue('');
                setNewLimit(UNLIMITED_VALUE);
              } else {
                const parsedValue = parseInt(numericValue, 10);
                setInputValue(parsedValue.toLocaleString('pt-BR'));
                setNewLimit(parsedValue);
              }
            }}
            onBlur={() => {
              // Quando sair do campo vazio, mostra ∞ para ficar claro que é ilimitado
              if (inputValue === '') {
                setInputValue('∞');
                setNewLimit(UNLIMITED_VALUE);
              }
            }}
            onKeyDown={(e) => {
              // Se pressionar backspace e o valor é ∞, limpa para permitir digitar
              if (e.key === 'Backspace' && inputValue === '∞') {
                e.preventDefault();
                setInputValue('');
                setNewLimit(UNLIMITED_VALUE);
              }
            }}
            placeholder="Digite um valor ou deixe vazio para ilimitado"
          />
          <p className="text-xs text-[#888888] mt-1">
            Use os presets abaixo ou digite um valor personalizado (deixe vazio para ilimitado)
          </p>
        </div>

        {/* Quick Presets */}
        <div className="mb-6">
          <label className="block text-xs text-[#888888] mb-2">
            Presets Rápidos
          </label>
          <div className="grid grid-cols-4 gap-2">
            <button
              onClick={() => {
                setNewLimit(100000);
                setInputValue('100.000');
              }}
              className={`
                px-3 py-2 text-sm rounded-lg transition-colors
                ${newLimit === 100000
                  ? 'bg-[#F4CC04] text-black font-semibold'
                  : 'bg-[#333333] hover:bg-[#444444] text-white'
                }
              `}
            >
              100k
            </button>
            <button
              onClick={() => {
                setNewLimit(500000);
                setInputValue('500.000');
              }}
              className={`
                px-3 py-2 text-sm rounded-lg transition-colors
                ${newLimit === 500000
                  ? 'bg-[#F4CC04] text-black font-semibold'
                  : 'bg-[#333333] hover:bg-[#444444] text-white'
                }
              `}
            >
              500k
            </button>
            <button
              onClick={() => {
                setNewLimit(1000000);
                setInputValue('1.000.000');
              }}
              className={`
                px-3 py-2 text-sm rounded-lg transition-colors
                ${newLimit === 1000000
                  ? 'bg-[#F4CC04] text-black font-semibold'
                  : 'bg-[#333333] hover:bg-[#444444] text-white'
                }
              `}
            >
              1M
            </button>
            <button
              onClick={() => {
                setNewLimit(UNLIMITED_VALUE);
                setInputValue('∞');
              }}
              className={`
                px-3 py-2 text-sm rounded-lg transition-colors
                ${newLimit === UNLIMITED_VALUE
                  ? 'bg-[#F4CC04] text-black font-semibold'
                  : 'bg-[#333333] hover:bg-[#444444] text-white'
                }
              `}
              title="Ilimitado"
            >
              ∞
            </button>
          </div>
        </div>

        {/* Reset Button */}
        <div className="mb-6 pt-4 border-t border-[#333333]">
          <button
            onClick={handleResetTokens}
            disabled={isResetting}
            className="
              w-full flex items-center justify-center gap-2
              px-4 py-3
              bg-red-500/20 hover:bg-red-500/30
              text-red-400
              rounded-lg
              transition-colors
              disabled:opacity-50 disabled:cursor-not-allowed
            "
          >
            <RefreshCw className={`w-4 h-4 ${isResetting ? 'animate-spin' : ''}`} />
            {isResetting ? 'Resetando...' : 'Resetar Contador de Tokens'}
          </button>
          <p className="text-xs text-center text-[#888888] mt-2">
            Isso irá zerar o contador de tokens usados, mantendo o limite
          </p>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3">
          <button
            onClick={onClose}
            className="
              flex-1 px-4 py-3
              bg-[#333333] hover:bg-[#444444]
              text-white
              rounded-lg
              transition-colors
            "
          >
            Cancelar
          </button>
          <button
            onClick={handleUpdateLimit}
            disabled={isUpdating}
            className="
              flex-1 px-4 py-3
              bg-[#F4CC04] hover:bg-[#e6ba04]
              text-black font-medium
              rounded-lg
              transition-colors
              disabled:opacity-50 disabled:cursor-not-allowed
            "
          >
            {isUpdating ? 'Salvando...' : 'Salvar Limite'}
          </button>
        </div>
      </div>
    </>
  );
};

export default TokenManagementModal;
